import React from 'react';
import { useTranslation } from 'react-i18next';
import { Leaf, Wind, Map as MapIcon, CheckCircle, FileText } from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import PageHeader from '../../components/PageHeader/PageHeader';
import Button from '../../components/Button/Button';
import styles from './Services.module.css';

// JSON-LD: ProfessionalService describing CINBIOSIS's offerings
const PROFESSIONAL_SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'CINBIOSIS',
  url: 'https://cinbiosis.com',
  description: 'Consultoría ambiental en Colombia: muestreo biológico, licenciamiento ANLA, sistemas MRV e inventarios GEI.',
  areaServed: 'Colombia',
  serviceType: [
    'Servicios Bióticos',
    'Licenciamiento Ambiental',
    'Gestión de Cambio Climático',
    'Sistemas MRV',
    'Inventarios GEI',
    'Servicios Geoespaciales',
    'Fotogrametría UAV',
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Portafolio de Servicios Ambientales CINBIOSIS',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Servicios Bióticos',
          description: 'Monitoreo, rescate y manejo de fauna y flora con trazabilidad georreferenciada, alineado a requerimientos de ANLA y CAR.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Licenciamiento Ambiental',
          description: 'Formulación de EIA, PMA e ICA bajo normativa colombiana con integración técnica y cartográfica.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Gestión de Cambio Climático y Sistemas MRV',
          description: 'Diseño de sistemas MRV, inventarios GEI bajo ISO 14064 e IPCC, financiamiento climático y estrategias de descarbonización.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Servicios Geoespaciales',
          description: 'Fotogrametría UAV, diseño de geodatabases SIG, modelos digitales del terreno y mapas temáticos de alta precisión.',
        },
      },
    ],
  },
};

// JSON-LD: FAQPage with common technical questions about CINBIOSIS services
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '¿Qué es un sistema MRV?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Un sistema MRV (Monitoreo, Reporte y Verificación) es un conjunto de procedimientos técnicos y metodológicos para cuantificar, reportar y verificar emisiones de gases de efecto invernadero (GEI) con trazabilidad institucional. En CINBIOSIS diseñamos sistemas MRV bajo estándares IPCC, ISO 14064 y GHG Protocol.',
      },
    },
    {
      '@type': 'Question',
      name: '¿Qué incluye el licenciamiento ambiental en Colombia?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'El licenciamiento ambiental en Colombia ante la ANLA incluye la formulación del Estudio de Impacto Ambiental (EIA), el Plan de Manejo Ambiental (PMA), los Informes de Cumplimiento Ambiental (ICA) y la integración de información cartográfica y SIG. CINBIOSIS acompaña cada etapa del proceso.',
      },
    },
    {
      '@type': 'Question',
      name: '¿En qué consisten los servicios bióticos de CINBIOSIS?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Los servicios bióticos de CINBIOSIS incluyen monitoreo de fauna y flora silvestre, rescate y reubicación de especies, inventarios forestales y diseño de planes de compensación ambiental con georreferenciación y trazabilidad de datos.',
      },
    },
    {
      '@type': 'Question',
      name: '¿Qué es la fotogrametría UAV y para qué se usa?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'La fotogrametría UAV utiliza drones para capturar imágenes aéreas de alta resolución que se procesan para generar modelos digitales del terreno, ortofotomosaicos y mapas temáticos. CINBIOSIS la aplica en seguimiento de polígonos de compensación ambiental e inventarios dendrológicos.',
      },
    },
  ],
};

const Services = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isEn = lang === 'en';
  const contactPath = `/${lang}/${isEn ? 'contact' : 'contacto'}`;

  const servicesList = [
    {
      id: "biotico",
      title: t('home.services.biotic.title'),
      icon: <Leaf size={36} />,
      desc: t('home.services.biotic.desc'),
      features: [t('home.services.biotic.f1'), t('home.services.biotic.f2'), t('home.services.biotic.f3'), t('home.services.biotic.f4')]
    },
    {
      id: "licenciamiento",
      title: t('home.services.licensing.title'),
      icon: <FileText size={36} />,
      desc: t('home.services.licensing.desc'),
      features: [t('home.services.licensing.f1'), t('home.services.licensing.f2'), t('home.services.licensing.f3'), t('home.services.licensing.f4')]
    },
    {
      id: "climatico",
      title: t('home.services.climate.title'),
      icon: <Wind size={36} />,
      desc: t('home.services.climate.desc'),
      features: [t('home.services.climate.f1'), t('home.services.climate.f2'), t('home.services.climate.f3'), t('home.services.climate.f4')]
    },
    {
      id: "geoespacial",
      title: t('home.services.geospatial.title'),
      icon: <MapIcon size={36} />,
      desc: t('home.services.geospatial.desc'),
      features: [t('home.services.geospatial.f1'), t('home.services.geospatial.f2'), t('home.services.geospatial.f3'), t('home.services.geospatial.f4')]
    }
  ];

  return (
    <div className={styles.servicesPage}>
      <SEO 
        title={t('seo.servicesTitle')}
        description={t('seo.servicesDesc')}
        canonicalPath={`/${isEn ? 'services' : 'servicios'}`}
        jsonLd={[PROFESSIONAL_SERVICE_SCHEMA, FAQ_SCHEMA]}
      />

      <PageHeader 
        title={t('servicesPage.headerTitle')} 
        description={t('servicesPage.headerDesc')}
      />

      <section className={styles.servicesSection}>
        <div className="container">
          <div className="text-center mb-4">
            <h2 className={styles.sectionTitle}>{t('servicesPage.sectionTitle')}</h2>
            <p className="text-muted" style={{maxWidth: '700px', margin: '0 auto'}}>
              {t('servicesPage.sectionSubtitle')}
            </p>
          </div>

          <div className={styles.cardsGrid}>
            {servicesList.map((service) => (
              <div key={service.id} className={styles.serviceCard}>
                <div className={styles.iconWrapper}>
                  {service.icon}
                </div>
                <h3>{service.title}</h3>
                <p className={styles.serviceDesc}>{service.desc}</p>
                <ul className={styles.featuresList}>
                  {service.features.map((feature, index) => (
                    <li key={index}>
                      <CheckCircle size={16} className={styles.checkIcon} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className={styles.cardActions}>
                  <Button to={contactPath} variant="primary" className={styles.btnFull}>{t('navbar.cta')}</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.ctaBanner}>
        <div className="container text-center">
          <h2 style={{color: 'white', marginBottom: '1rem'}}>{t('servicesPage.ctaTitle')}</h2>
          <p style={{color: 'rgba(255,255,255,0.75)', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem'}}>
            {t('servicesPage.ctaSubtitle')}
          </p>
          <Button to={contactPath} variant="outline" style={{borderColor: 'rgba(255,255,255,0.5)', color: 'white'}}>
            {t('servicesPage.ctaBtn')}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;



